import { Currency } from 'api/v1/market'
import {
  BlockedDepositChains,
  DefaultDepositEstimateMinutes,
  DepositEstimateMinutes,
  DepositPagePath,
} from 'constants/deposit'
import { compact, concat, unionBy } from 'lodash'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useGateways, useSupportChains } from 'store/app/hooks'
import { useSpotCurrencies } from 'store/market/hooks'
import { Ether, L1ChainId, L1TxHash } from 'types'
import {
  DepositETHTransaction,
  DepositModalOption,
  DepositStatus,
} from './types'
import { useMatch } from 'react-router-dom'

export function useDepositStatus(ethHash: string) {
  return useSelector<RootState, DepositStatus>(
    (state) => state.deposit.depositStatus[ethHash],
  )
}
export function useAllDepositStatus() {
  return useSelector<RootState, Record<string, DepositStatus>>(
    (state) => state.deposit.depositStatus,
  )
}
export function useTxConfirmationsByHash(txHash: L1TxHash) {
  return useSelector<RootState, number>(
    (state) => state.deposit.txConfirmations[txHash] ?? 0,
  )
}
export function usePendingTxs() {
  return useSelector<RootState, DepositETHTransaction[]>(
    (state) => state.deposit.pendingTxs,
  )
}
export function useQueryL2Hash() {
  return useSelector<RootState, string[]>((state) => state.deposit.queryL2Hash)
}
export function useDepositModalOption() {
  return useSelector<RootState, DepositModalOption>(
    (state) => state.deposit.modalOption,
  )
}
// Access from '/deposit/:address'
export function useIsDepositPage() {
  return !!useMatch(DepositPagePath)
}
export function getDepositsEstimateMinutes(chainId: L1ChainId): number {
  return DepositEstimateMinutes[chainId] ?? DefaultDepositEstimateMinutes
}
export function useDepositing() {
  return useSelector<RootState, boolean>((state) => state.deposit.depositing)
}
export function useDepositAmount() {
  return useSelector<RootState, Ether>((state) => state.deposit.amount)
}
export function useDepositToken() {
  return useSelector<RootState, Currency | undefined>(
    (state) => state.deposit.selectedToken,
  )
}
export function isDepositBlockedChain(chainId: L1ChainId) {
  return BlockedDepositChains.includes(chainId)
}
export const useTokenListOnDeposit = (l1ChainId?: L1ChainId) => {
  const supportChains = useSupportChains()
  const gateways = useGateways()
  const currencies = useSpotCurrencies()

  return useMemo(() => {
    if (!l1ChainId) {
      return []
    }
    const chain = supportChains.find((v) => v.layerOneChainId === l1ChainId)
    if (!chain) {
      return []
    }

    // If this chian is marked gateway, using the 'tokens' value to filter all token from currencies
    if (chain.gateway) {
      // Get all gateway supported tokens
      const gatewayTokens = unionBy(
        concat(...gateways.map((v) => v.tokens)),
        'tokenId',
      )
      return compact(
        gatewayTokens.map((token) => {
          const currency = currencies.find(
            (currency) => currency.l2CurrencyId === token.tokenId,
          )
          return currency!
        }),
      )
    }
    // On the source chain using the 'currecies[i].chains[layerTwoChainId]' to filter all currencies
    else {
      return currencies.filter(
        (v) => v.chains && v.chains[chain.layerTwoChainId] !== undefined,
      )
    }
  }, [currencies, supportChains, gateways, l1ChainId])
}
